
































































































import Vue, { VueConstructor } from 'vue';
import colors from 'vuetify/es5/util/colors';
import AlertDesigner from '@/components/AlertDesigner.vue';
import { loadingService, realmService, wowItemService } from '@/services';
import { UserMixin } from '@/mixins/UserMixin';
import { Subscription } from 'rxjs';
import { Alert, AlertConditionMetric, AlertState, ConnectedRealm, Realm, WoWItem } from '@/models';
import { alertService } from '@/services/AlertService';
import { Utilities } from '@/utilities';
import { List } from 'typescript-extended-linq';

export default (Vue as VueConstructor<Vue & InstanceType<typeof UserMixin>>).extend({
  name: 'Alerts',

  components: {
    AlertDesigner
  },

  mixins: [UserMixin],

  data: () => ({
    pageLoading: false,
    loadingSubscription: new Subscription(),
    alerts: new List<Alert>(),
    selectedAlert: null as Alert | null,
    showDeleteDialog: false,
    stagedAlertToDelete: null as Alert | null,
    items: new Map<number, WoWItem>(),
    realms: [] as Realm[],
    realmsLookup: new Map<number, Realm>(),
    connectedRealms: new Map<number, ConnectedRealm>(),
    showCreateAlertDialogField: false
  }),

  methods: {
    cancelDeleteStagedAlert(): void {
      this.stagedAlertToDelete = null;
      this.showDeleteDialog = false;
    },

    async deleteStagedAlert(): Promise<void> {
      if (!this.stagedAlertToDelete) {
        return;
      }

      try {
        await alertService.deleteAlertForUser(this.userId, this.stagedAlertToDelete.id);
        this.alerts.remove(this.stagedAlertToDelete);
      } catch (error) {
        console.error(error);
      } finally {
        this.stagedAlertToDelete = null;
        this.showDeleteDialog = false;
      }
    },

    getAlertColor(alert: Alert): string {
      switch (alert.state) {
        case AlertState.InsufficientData:
          return colors.amber.darken2;
        case AlertState.Alarm:
          return colors.red.darken2;
        case AlertState.Ok:
          return colors.green.darken2;
        default:
          throw new Error(`${alert.state} is an invalid state.`);
      }
    },

    splitAtUpperCase(str: string): string {
      return Utilities.splitAtUpperCase(str);
    },

    onAlertUpdated(updatedAlert: Alert): void {
      const current = this.alerts.indexOf(this.alerts.first(alert => alert.id === updatedAlert.id));
      this.alerts.insert(current, updatedAlert);
      this.alerts.removeAt(current + 1);
    },

    onAlertCreated(newAlert: Alert): void {
      this.alerts.add(newAlert);
    },

    getConditionsString(alert: Alert): string {
      return alert.conditions.reduce((prev, curr, i) => {
        const item = this.items.get(curr.wowItemId);
        const realms = this.connectedRealms.get(curr.connectedRealmId)?.realms ?? [];
        const { g, s, c } = Utilities.convertToGoldSilverCopper(curr.threshold);

        return `${prev} ${Utilities.splitAtUpperCase(
          curr.aggregationType
        ).toLowerCase()} of ${Utilities.splitAtUpperCase(
          curr.metric
        ).toLowerCase()} for <a href="https://www.wowhead.com/item=${item?.id}" target="_blank">${
          item?.name
        }</a> for ${realms.reduce(
          (prevStr, currRealm, j) =>
            `${prevStr}${currRealm.name}${
              j !== realms.length - 1
                ? `${j === realms.length - 2 ? `${realms.length > 2 ? ', and ' : ' and '}` : ', '}`
                : ''
            }`,
          ''
        )} is ${Utilities.splitAtUpperCase(curr.operator).toLowerCase()} ${
          curr.metric === AlertConditionMetric.TotalAvailableForAuction
            ? curr.threshold.toLocaleString('en-US')
            : `${g.toLocaleString('en-US')}g ${s}s ${c}c`
        } over period of ${curr.aggregationTimeGranularityInHours} hour${
          curr.aggregationTimeGranularityInHours === 1 ? '' : 's'
        }${i === alert.conditions.length - 1 ? '.' : ' AND when'}`;
      }, 'When');
    }
  },

  computed: {
    showCreateAlertDialog: {
      get(): boolean {
        return this.showCreateAlertDialogField;
      },

      set(value: boolean): void {
        if (!value) {
          this.selectedAlert = null;
        }

        this.showCreateAlertDialogField = value;
      }
    }
  },

  async mounted(): Promise<void> {
    this.loadingSubscription = loadingService.loadingStateChanged.subscribe(loading => (this.pageLoading = loading));
    loadingService.startLoading();

    const realmsPromise = realmService.getConnectedRealms();
    const alerts = await alertService.getAlertsForUser(this.userId);
    const connectedRealms = await realmsPromise;
    const items = await Promise.all(
      alerts
        .selectMany(alert => alert.conditions)
        .select(condition => condition.wowItemId)
        .distinct()
        .select(itemId => wowItemService.getItem(itemId))
    );

    for (const item of items) {
      if (item) {
        this.items.set(item.id, item);
      }
    }

    this.realms = connectedRealms
      .pipe(connectedRealm => this.connectedRealms.set(connectedRealm.id, connectedRealm))
      .selectMany(r => r.realms)
      .pipe(realm => this.realmsLookup.set(realm.id, realm))
      .orderBy(realm => realm.name)
      .toArray();

    this.alerts = alerts;

    loadingService.stopLoading();
  },

  beforeDestroy(): void {
    this.loadingSubscription.unsubscribe();
  }
});
